import React, {useEffect, useState} from 'react';
import MaterialTable from './MaterialTable/MaterialTable';
import Dialog from '@material-ui/core/Dialog';
import {
    KeyboardDatePicker,
    MuiPickersUtilsProvider
} from '@material-ui/pickers';
import LuxonUtils from '@date-io/luxon';
import {MuiThemeProvider} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import ConversationDetail from './ConversationDetail/ConversationDetail';
import Slide from '@material-ui/core/Slide';
import AutocompleatableSelect from '../Select/AutocompleatableSelect';
import CoachingKangaroo from '../../api/CoachingKangaroo';
import TrainingToolTheme from '../TrainingToolTheme';
import ConversationStatusSelect from '../ConversationStatusSelect/ConversationStatusSelect';
import ConversationFeelingSelect from '../ConversationFeelingSelect/ConversationFeelingSelect';
import {ratingCodes} from './ConversationItem/AnswerRatingButtons/AnswerRatingButtons';
import TextField from '@material-ui/core/TextField';
import {
    formatDateToYyyyMmDd,
    TIME_FROM,
    TIME_TO
} from '../../utils/formattingUtils';
import useAlert from '../hooks/useAlert';
import RangeSelect, {EDGES} from '../Select/RangeSelect';
import {useTranslation} from 'react-i18next';
import IconButton from '@material-ui/core/IconButton';
import {Clear} from '@material-ui/icons';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import parse from 'html-react-parser';
import CloseIcon from '@material-ui/icons/Close';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from "@material-ui/core/Button";
import {buildUrlFromFilter, buildFilterFromUrl} from "../../services/urlFilterService";
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Switch from '@material-ui/core/Switch';
import Tooltip from '@material-ui/core/Tooltip';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const FilterSection = ({title, first = false, showIcon = false, children}) => (
    <div className={`filter-section${first ? ' filter-section--first' : ''}`}>
        <h4 className="filter-section-heading">
            <span>{title}</span>
            {showIcon && (
                <img
                    src="./ai_avatar.png"
                    alt=""
                    className="filter-section-heading-icon"
                    aria-hidden="true"
                />
            )}
        </h4>
        <Grid container spacing={2}>
            {children}
        </Grid>
    </div>
);

const statuses = [
    'NOT_RATED',
    'RATED',
    'TRAINING_NEEDED',
    'TRAINED',
    'RESOLVED'
];

const aiFilters = {
    aiRated: 'AI Rated',
    aiNotRated: 'AI NOT Rated',
    noAiFilter: 'NO AI Filter'
};

const feelings = ['NONE', 'GOOD', 'BAD'];

const platforms = [
    'WEB_PUBLIC',
    'MOBILE_APP',
    'IM',
    'PHONE',
    'GOOGLE_ASSISTANT'
];

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const MaterialTrainingTool = (props) => {
    const {renderFilters = false, renderTable = false} = props;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [conversationPreview, setConversationPreview] = useState(null);
    const [dirtyConversation, setDirtyConversation] = useState(null);
    const [conversationList, setConversationList] = useState([]);
    const [conversationIndex, setConversationIndex] = useState(null);
    const [conversationGlobalIndex, setConversationGlobalIndex] = useState(null);
    const [hasNextPageGlobal, setHasNextPageGlobal] = useState(false);
    const [inputSessionID, setInputSessionID] = useState('');
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d;
    });
    const [dateTo, setDateTo] = useState(new Date());
    const [intents, setIntents] = useState([]);
    const [entities, setEntities] = useState([]);
    const [context, setContext] = useState(props.context);
    const [contextValues, setContextValues] = useState([]);
    const [entityValues, setEntityValues] = useState([]);
    const [selectedIntents, setSelectedIntents] = useState([]);
    const [selectedEntities, setSelectedEntities] = useState([]);
    const [selectedEntityValues, setSelectedEntityValues] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedFeelings, setSelectedFeelings] = useState([]);
    const [selectedRatings, setSelectedRatings] = useState([]);
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [selectedContext, setSelectedContext] = useState([]);
    const [selectedContextValues, setSelectedContextValues] = useState([]);
    const [filter, setFilter] = useState(null);
    const [scoreFrom, setScoreFrom] = useState(EDGES.min);
    const [scoreTo, setScoreTo] = useState(EDGES.max);
    const [fulltext, setFulltext] = useState('');
    const [category, setCategory] = useState([]);
    const [aiFilter, setAiFilter] = useState(aiFilters.noAiFilter);
    const {Alert, status, showError} = useAlert();
    const {project, projectId} = props;
    const {t} = useTranslation();
    const [aiDialog, setAiDialog] = useState(false);
    const [aiReport] = useState('');
    const [minimalAnswerCount, setMinimalAnswerCount] = useState(0);
    const [categoriesOptions, setCategoriesOptions] = useState([]);
    const [clientNameOptions, setClientNameOptions] = useState([]);
    const [selectedClientNames, setSelectedClientNames] = useState([]);
    const [filtersVisible, setFiltersVisible] = useState(true);
    const [dateRangeChanged, setDateRangeChanged] = useState(false);
    const [hasInitialFilter, setHasInitialFilter] = useState(false);
    const [initialFilterChecked, setInitialFilterChecked] = useState(false);
    const [randomSampleEnabled, setRandomSampleEnabled] = useState(false);
    const [randomSampleSize, setRandomSampleSize] = useState(50);

    useEffect(() => {
        const fromUrl = buildFilterFromUrl();
        if (fromUrl && Object.keys(fromUrl).length > 0) {
            const normalized = {
                dateTo: fromUrl.dateTo ? new Date(fromUrl.dateTo) : dateTo,
                dateFrom: fromUrl.dateFrom ? new Date(fromUrl.dateFrom) : dateFrom,
                selectedIntents: fromUrl.selectedIntents || [],
                selectedEntities: fromUrl.selectedEntities || [],
                selectedEntityValues: fromUrl.selectedEntityValues || [],
                selectedStatuses: fromUrl.selectedStatuses || [],
                selectedFeelings: fromUrl.selectedFeelings || [],
                selectedRatings: fromUrl.selectedRatings || [],
                inputSessionID: fromUrl.inputSessionID || '',
                selectedPlatforms: fromUrl.selectedPlatforms || [],
                selectedContext: fromUrl.selectedContext || [],
                selectedContextValues: fromUrl.selectedContextValues || [],
                scoreFrom: fromUrl.scoreFrom !== undefined ? Number(fromUrl.scoreFrom) : scoreFrom,
                scoreTo: fromUrl.scoreTo !== undefined ? Number(fromUrl.scoreTo) : scoreTo,
                fulltext: fromUrl.fulltext || '',
                aiFilter: fromUrl.aiFilter || aiFilter,
                minimalAnswerCount: fromUrl.minimalAnswerCount !== undefined ? Number(fromUrl.minimalAnswerCount) : minimalAnswerCount,
                category: fromUrl.category || [],
                clientNames: fromUrl.clientNames || [],
                randomSampleEnabled: !!fromUrl.randomSampleEnabled,
                randomSampleSize: fromUrl.randomSampleSize !== undefined
                    ? Number(fromUrl.randomSampleSize)
                    : 50
            };

            setDateTo(normalized.dateTo);
            setDateFrom(normalized.dateFrom);
            setSelectedIntents(normalized.selectedIntents);
            setSelectedEntities(normalized.selectedEntities);
            setSelectedEntityValues(normalized.selectedEntityValues);
            setSelectedStatuses(normalized.selectedStatuses);
            setSelectedFeelings(normalized.selectedFeelings);
            setSelectedRatings(normalized.selectedRatings);
            setInputSessionID(normalized.inputSessionID);
            setSelectedPlatforms(normalized.selectedPlatforms);
            setSelectedContext(normalized.selectedContext);
            setSelectedContextValues(normalized.selectedContextValues);
            setScoreFrom(normalized.scoreFrom);
            setScoreTo(normalized.scoreTo);
            setFulltext(normalized.fulltext);
            setAiFilter(normalized.aiFilter);
            setMinimalAnswerCount(normalized.minimalAnswerCount);
            setCategory(normalized.category);
            setSelectedClientNames(normalized.clientNames);
            setRandomSampleEnabled(normalized.randomSampleEnabled);
            setRandomSampleSize(normalized.randomSampleSize);
            setFilter(normalized);
            setHasInitialFilter(true);
        }
        setInitialFilterChecked(true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!projectId) return;
        CoachingKangaroo.get(`api/v1/projects/${projectId}/clients`).then(response => {
            let clients = Array.isArray(response.data) ? response.data.map(it => it.name) : []
            clients = Array.from(new Set(clients))
            setClientNameOptions(clients);
        })
    }, [projectId]);

    useEffect(() => {
        if (!projectId) return;
        if (initialFilterChecked && !hasInitialFilter) {
            filterReset();
        }
        getCategories();
        getEntities();
        getIntents();
        getContext();
    }, [projectId, hasInitialFilter, initialFilterChecked]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (dateRangeChanged) {
            filterSubmit();
            setDateRangeChanged(false);
        }
    }, [dateFrom, dateTo, dateRangeChanged]); // eslint-disable-line react-hooks/exhaustive-deps

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (!projectId) return;
        if (selectedEntities.length > 0) {
            CoachingKangaroo.get(
                '/api/training/v1/' +
                encodeURIComponent(projectId) +
                '/entity-values?entities=' +
                selectedEntities +
                '&at_from=' +
                formatDateToYyyyMmDd(dateFrom) +
                TIME_FROM +
                '&at_to=' +
                formatDateToYyyyMmDd(dateTo) +
                TIME_TO
            )
                .then((response) => {
                    setEntityValues(response.data);
                })
                .catch(() => {});
        } else {
            setEntityValues([]);
            setSelectedEntityValues([]);
        }
    }, [selectedEntities]);
    /* eslint-enable react-hooks/exhaustive-deps */

    useEffect(() => {
        if (!projectId) return;
        if (selectedContext.length > 0) {
            CoachingKangaroo.get(
                '/api/training/v1/' +
                encodeURIComponent(projectId) +
                '/context-values?context=' +
                selectedContext +
                '&at_from=' +
                formatDateToYyyyMmDd(dateFrom) +
                TIME_FROM +
                '&at_to=' +
                formatDateToYyyyMmDd(dateTo) +
                TIME_TO
            )
                .then((response) => {
                    setContextValues(response.data);
                })
                .catch(() => {});
        } else {
            setContextValues([]);
            setSelectedContextValues([]);
        }
    }, [selectedContext]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        function handleEscapeKey(event) {
            if (event.code === 'Escape') {
                handleDialogClose();
            }
        }

        document.addEventListener('keydown', handleEscapeKey);
        return () => document.removeEventListener('keydown', handleEscapeKey);
    }, []);

    const rowClicked = (row, index, list, hasNextPageFromTable, pageFromTable, rowsPerPageFromTable) => {
        setConversationPreview(row);
        const safeList = Array.isArray(list) ? list : [];
        setConversationList(safeList);
        const localIndex = typeof index === 'number' ? index : null;
        setConversationIndex(localIndex);

        if (typeof localIndex === 'number' && typeof pageFromTable === 'number' && typeof rowsPerPageFromTable === 'number') {
            const globalIndex = pageFromTable * rowsPerPageFromTable + localIndex;
            setConversationGlobalIndex(globalIndex);
        } else {
            setConversationGlobalIndex(null);
        }

        setHasNextPageGlobal(!!hasNextPageFromTable);
        setDialogOpen(true);
    };

    const showConversationAtIndex = (index, globalIndex) => {
        if (!Array.isArray(conversationList) || index < 0 || index >= conversationList.length) {
            return;
        }
        setConversationPreview(conversationList[index]);
        setConversationIndex(index);
        if (typeof globalIndex === 'number') {
            setConversationGlobalIndex(globalIndex);
        }
    };

    const buildJSONFilterForApi = (currentFilter) => {
        const filterToUse = currentFilter || buildCurrentFilter();
        let dto = {};

        if (filterToUse.dateFrom) {
            dto.at_from = formatDateToYyyyMmDd(filterToUse.dateFrom) + TIME_FROM;
        } else {
            let date = new Date();
            date.setDate(date.getDate() - 1);
            dto.at_from = formatDateToYyyyMmDd(date) + TIME_FROM;
        }

        if (filterToUse.dateTo) {
            dto.at_to = formatDateToYyyyMmDd(filterToUse.dateTo) + TIME_TO;
        } else {
            dto.at_to = formatDateToYyyyMmDd(new Date()) + TIME_TO;
        }

        if (filterToUse.selectedIntents && filterToUse.selectedIntents.length > 0) {
            dto.intents = filterToUse.selectedIntents;
        }
        if (filterToUse.selectedEntities && filterToUse.selectedEntities.length > 0) {
            dto.entities = filterToUse.selectedEntities;
        }
        if (filterToUse.selectedEntityValues && filterToUse.selectedEntityValues.length > 0) {
            dto.entityValues = filterToUse.selectedEntityValues;
        }
        if (filterToUse.selectedStatuses && filterToUse.selectedStatuses.length > 0) {
            dto.statuses = filterToUse.selectedStatuses;
        }
        if (filterToUse.selectedFeelings && filterToUse.selectedFeelings.length > 0) {
            dto.feelings = filterToUse.selectedFeelings;
        }
        if (filterToUse.selectedRatings && filterToUse.selectedRatings.length > 0) {
            dto.ratings = filterToUse.selectedRatings;
        }
        if (filterToUse.selectedPlatforms && filterToUse.selectedPlatforms.length > 0) {
            dto.platforms = filterToUse.selectedPlatforms;
        }
        if (filterToUse.selectedContext && filterToUse.selectedContext.length > 0) {
            dto.context = filterToUse.selectedContext;
        }
        if (filterToUse.selectedContextValues && filterToUse.selectedContextValues.length > 0) {
            dto.contextValues = filterToUse.selectedContextValues;
        }
        if (filterToUse.scoreFrom) {
            dto.score_from = filterToUse.scoreFrom;
        }
        if (filterToUse.scoreTo) {
            dto.score_to = filterToUse.scoreTo;
        }
        if (filterToUse.fulltext) {
            dto.fulltext = filterToUse.fulltext;
        }
        if (filterToUse.aiFilter && filterToUse.aiFilter !== 'NO AI Filter') {
            dto.ai_filter = '';
            if (filterToUse.aiFilter === 'AI Rated') {
                dto.ai_filter = 'AI_RATED';
            } else {
                dto.ai_filter = 'AI_NOT_RATED';
            }
        }
        if (filterToUse.minimalAnswerCount) {
            dto.answerCount = filterToUse.minimalAnswerCount;
        }
        if (filterToUse.categories && filterToUse.categories.length > 0) {
            dto.categories = filterToUse.categories;
        } else if (filterToUse.category && filterToUse.category.length > 0) {
            dto.categories = filterToUse.category;
        }
        if (filterToUse.clientNames && filterToUse.clientNames.length > 0) {
            dto.clientNames = filterToUse.clientNames;
        }

        return dto;
    };

    const fetchConversationByCursor = async (lastSessionId, lastCreatedAt) => {
        const currentFilter = filter ?? buildCurrentFilter();
        const dto = buildJSONFilterForApi(currentFilter);
        dto.limit = 1;
        dto.offset = 0;
        dto.total = false;
        dto.last_session_id = lastSessionId;
        dto.last_created_at = lastCreatedAt;

        const response = await CoachingKangaroo.post(
            '/api/training/v1/' +
            encodeURIComponent(projectId) +
            '/conversations',
            dto
        );

        if (response.data && Array.isArray(response.data.conversations)) {
            return response.data.conversations;
        }
        return [];
    };

    const fetchConversationByOffset = async (offset) => {
        const currentFilter = filter ?? buildCurrentFilter();
        const dto = buildJSONFilterForApi(currentFilter);
        dto.limit = 1;
        dto.offset = offset;
        dto.total = false;

        const response = await CoachingKangaroo.post(
            '/api/training/v1/' +
            encodeURIComponent(projectId) +
            '/conversations',
            dto
        );

        if (response.data && Array.isArray(response.data.conversations)) {
            return response.data.conversations;
        }
        return [];
    };

    const handleShowPreviousConversation = async () => {
        if (conversationIndex === null || conversationGlobalIndex === null) return;

        if (conversationIndex > 0) {
            const newIndex = conversationIndex - 1;
            const newGlobalIndex = conversationGlobalIndex - 1;
            showConversationAtIndex(newIndex, newGlobalIndex);
            return;
        }

        if (conversationGlobalIndex <= 0) return;

        try {
            const targetOffset = conversationGlobalIndex - 1;
            const conversations = await fetchConversationByOffset(targetOffset);
            if (conversations.length === 0) return;
            const previousConversation = conversations[0];
            setConversationList((prev) => [previousConversation, ...prev]);
            setConversationIndex(0);
            setConversationGlobalIndex(targetOffset);
            setConversationPreview(previousConversation);
        } catch {
        }
    };

    const handleShowNextConversation = async () => {
        if (conversationIndex === null || conversationGlobalIndex === null) return;

        if (conversationIndex < conversationList.length - 1) {
            const newIndex = conversationIndex + 1;
            const newGlobalIndex = conversationGlobalIndex + 1;
            showConversationAtIndex(newIndex, newGlobalIndex);
            return;
        }

        if (!hasNextPageGlobal) return;

        const currentItem = conversationPreview;
        if (!currentItem?.id || !currentItem?.['started-at']) return;

        try {
            const conversations = await fetchConversationByCursor(currentItem.id, currentItem['started-at']);
            if (conversations.length === 0) {
                setHasNextPageGlobal(false);
                return;
            }
            const nextConversation = conversations[0];
            setConversationList((prev) => [...prev, nextConversation]);
            setConversationIndex(
                typeof conversationIndex === 'number' ? conversationIndex + 1 : conversationList.length
            );
            setConversationGlobalIndex(conversationGlobalIndex + 1);
            setConversationPreview(nextConversation);
        } catch {
        }
    };

    const hasPreviousConversation =
        conversationIndex !== null &&
        conversationGlobalIndex !== null &&
        conversationGlobalIndex > 0;

    const hasNextConversation =
        conversationIndex !== null &&
        Array.isArray(conversationList) &&
        (conversationIndex < conversationList.length - 1 || hasNextPageGlobal);

    const getContext = () => {
        CoachingKangaroo.get(
            '/api/training/v1/' +
            encodeURIComponent(projectId) +
            '/context' +
            '?at_from=' +
            formatDateToYyyyMmDd(dateFrom) +
            TIME_FROM +
            '&at_to=' +
            formatDateToYyyyMmDd(dateTo) +
            TIME_TO
        )
            .then((response) => {
                const result = response.data;
                if (result) {
                    setContext(result);
                }
            })
            .catch((error) => {
                showError('Error while fetching context', error);
                setContext([]);
            });
    };

    const getCategories = () => {
        CoachingKangaroo.get(
            `api/training/v1/${encodeURIComponent(projectId)}/autocomplete?type=category&prefix=`
        )
            .then((response) => {
                setCategoriesOptions(Array.isArray(response.data) ? response.data : []);
            })
            .catch(() => {
                setCategoriesOptions([]);
            });
    };

    const getIntents = () => {
        CoachingKangaroo.get(
            '/api/training/v1/' +
            encodeURIComponent(projectId) +
            '/intents' +
            '?at_from=' +
            formatDateToYyyyMmDd(dateFrom) +
            TIME_FROM +
            '&at_to=' +
            formatDateToYyyyMmDd(dateTo) +
            TIME_TO
        )
            .then((response) => {
                setIntents(response.data);
            })
            .catch(() => {
                setIntents([]);
            });
    };

    const getEntities = () => {
        CoachingKangaroo.get(
            '/api/training/v1/' +
            encodeURIComponent(projectId) +
            '/entities' +
            '?at_from=' +
            formatDateToYyyyMmDd(dateFrom) +
            TIME_FROM +
            '&at_to=' +
            formatDateToYyyyMmDd(dateTo) +
            TIME_TO
        )
            .then((response) => {
                const result = response.data;
                if (result) {
                    setEntities(result);
                }
            })
            .catch(() => {
                setEntities([]);
            });
    };

    const buildCurrentFilter = () => ({
            dateFrom: dateFrom,
            dateTo: dateTo,
            selectedIntents: selectedIntents,
            selectedEntities: selectedEntities,
            selectedEntityValues: selectedEntityValues,
            selectedStatuses: selectedStatuses,
            selectedFeelings: selectedFeelings,
            selectedRatings: selectedRatings,
            inputSessionID: inputSessionID,
            selectedPlatforms: selectedPlatforms,
            selectedContext: selectedContext,
            selectedContextValues: selectedContextValues,
            scoreFrom: scoreFrom / 100,
            scoreTo: scoreTo / 100,
            fulltext: fulltext,
            category: category,
            aiFilter: aiFilter,
            minimalAnswerCount: minimalAnswerCount,
            clientNames: selectedClientNames,
            randomSampleEnabled: randomSampleEnabled,
            randomSampleSize: randomSampleSize,
    });

    const filterSubmit = () => {
        setFilter(buildCurrentFilter());
    };

    const setDateRange = (days) => {
        const today = new Date();
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - days);
        fromDate.setHours(0, 0, 0, 0);
        today.setHours(23, 59, 59, 999);
        
        setDateFrom(fromDate);
        setDateTo(today);
        setDateRangeChanged(true);
    };

    const filterReset = () => {
        const d = new Date();
        const yesterday = new Date();
        yesterday.setDate(d.getDate() - 1);
        
        setDateFrom(yesterday);
        setDateTo(d);
        setSelectedIntents([]);
        setSelectedEntities([]);
        setSelectedEntityValues([]);
        setSelectedStatuses([]);
        setSelectedFeelings([]);
        setSelectedRatings([]);
        setInputSessionID('');
        setSelectedPlatforms([]);
        setSelectedContext([]);
        setSelectedContextValues([]);
        setScoreFrom(EDGES.min);
        setScoreTo(EDGES.max);
        setFulltext('');
        setCategory([]);
        setAiFilter(aiFilters.noAiFilter);
        setMinimalAnswerCount(0);
        setSelectedClientNames([]);
        setRandomSampleEnabled(false);
        setRandomSampleSize(50);
        setFilter({
            dateFrom: yesterday,
            dateTo: d,
            selectedIntents: [],
            selectedEntities: [],
            selectedEntityValues: [],
            selectedStatuses: [],
            selectedFeelings: [],
            selectedRatings: [],
            inputSessionID: '',
            selectedPlatforms: [],
            selectedContext: [],
            selectedContextValues: [],
            scoreFrom: EDGES.min / 100,
            scoreTo: EDGES.max / 100,
            fulltext: '',
            category: [],
            aiFilter: aiFilters.noAiFilter,
            minimalAnswerCount: 0,
            clientNames: [],
            randomSampleEnabled: false,
            randomSampleSize: 50,
        });
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const handleSessionIDChange = (event) => {
        setInputSessionID(event.target.value.trim());
    };

    const copyPathToClipboard = () => {
        const currentFilter = filter ?? buildCurrentFilter();
        const url = buildUrlFromFilter(currentFilter);
        navigator.clipboard.writeText(url).catch(() => {});
    }
    const filtersContent = (
        <div className="flex flex-col h-full bg-white p-4">
            {status && <Alert severity="info">{status}</Alert>}
            <MuiThemeProvider theme={TrainingToolTheme}>
                <div
                    className="flex w-full justify-between items-start flex-col mb-4 z-40 border-b border-[#d9d9d9] pb-4">
                    <div className="flex w-full justify-between items-center mb-4">
                        <h3 className="mb-0 text-xl font-normal">{t('Filters')}</h3>
                        <IconButton
                            onClick={() => setFiltersVisible(!filtersVisible)}
                            style={{
                                padding: '4px',
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px'
                            }}
                            aria-label={t('Hide filters')}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                    </div>
                    <div className="flex flex-row gap-2 w-full">
                        <button
                            className="py-2 px-4 bg-[#58a813] rounded-lg font-normal text-base hover:bg-[#4f9711] transition-all flex-1"
                            onClick={filterSubmit}
                        >
                            {t('Filter')}
                        </button>
                        <button
                            className="py-2 px-4 bg-[#e0e0e0] rounded-lg font-normal text-base hover:bg-[#cacaca] transition-all flex-1"
                            onClick={filterReset}
                        >
                            {t('Reset')}
                        </button>
                    </div>
                </div>

                <div className="filter-sections">
                    <FilterSection first title={t('Basic filters')}>
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <MuiPickersUtilsProvider utils={LuxonUtils}>
                                    <KeyboardDatePicker
                                        fullWidth
                                        label={t('Date From')}
                                        value={dateFrom}
                                        onChange={setDateFrom}
                                        clearable
                                        disableFuture
                                        format="dd.MM.yyyy"
                                        autoOk
                                        KeyboardButtonProps={{
                                            style: {
                                                padding: 3
                                            }
                                        }}
                                    />
                                </MuiPickersUtilsProvider>
                            </Grid>
                            <Grid item xs={6}>
                                <MuiPickersUtilsProvider utils={LuxonUtils}>
                                    <KeyboardDatePicker
                                        fullWidth
                                        label={t('Date To')}
                                        value={dateTo}
                                        onChange={setDateTo}
                                        clearable
                                        disableFuture
                                        format="dd.MM.yyyy"
                                        autoOk
                                        KeyboardButtonProps={{
                                            style: {
                                                padding: 3
                                            }
                                        }}
                                    />
                                </MuiPickersUtilsProvider>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={randomSampleEnabled}
                                    onChange={(e) => setRandomSampleEnabled(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    {t('Random sample')}
                                    <Tooltip title={t('Random sample tooltip')} arrow>
                                        <InfoOutlinedIcon
                                            fontSize="small"
                                            className="random-sample-tooltip-icon"
                                        />
                                    </Tooltip>
                                </span>
                            }
                        />
                    </Grid>
                    {randomSampleEnabled && (
                        <Grid item xs={12}>
                            <TextField
                                label={t('Sample size (20-100)')}
                                type="number"
                                fullWidth
                                inputProps={{ min: 20, max: 100 }}
                                value={randomSampleSize}
                                onChange={(e) => {
                                    const value = Number(e.target.value) || 0;
                                    setRandomSampleSize(value);
                                }}
                                error={randomSampleSize < 20 || randomSampleSize > 100}
                                helperText={
                                    randomSampleSize < 20 || randomSampleSize > 100
                                        ? t('Sample size out of range')
                                        : ''
                                }
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <div className="flex flex-row gap-2 flex-wrap">
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setDateRange(7)}
                                style={{ fontSize: '0.75rem', padding: '4px 8px' }}
                            >
                                {t('Last 7 days')}
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setDateRange(14)}
                                style={{ fontSize: '0.75rem', padding: '4px 8px' }}
                            >
                                {t('Last 14 days')}
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setDateRange(30)}
                                style={{ fontSize: '0.75rem', padding: '4px 8px' }}
                            >
                                {t('Last 30 days')}
                            </Button>
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleatableSelect
                            label={t('Assistant')}
                            options={clientNameOptions}
                            noOptionsText={t('empty_client_names')}
                            selectedOptions={selectedClientNames}
                            setSelectedOptions={setSelectedClientNames}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={t('Session ID')}
                            fullWidth
                            onChange={handleSessionIDChange}
                            value={inputSessionID}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        style={{visibility: inputSessionID ? 'visible' : 'hidden'}}
                                        aria-label="delete"
                                        size="small"
                                        onClick={() => setInputSessionID('')}
                                    >
                                        <Clear/>
                                    </IconButton>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleatableSelect
                            label={t('Platforms')}
                            options={platforms}
                            selectedOptions={selectedPlatforms}
                            setSelectedOptions={setSelectedPlatforms}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={t('Fulltext search')}
                            fullWidth
                            onChange={(e) => setFulltext(e.target.value)}
                            value={fulltext}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        style={{visibility: fulltext ? 'visible' : 'hidden'}}
                                        aria-label="delete"
                                        size="small"
                                        onClick={() => setFulltext('')}
                                    >
                                        <Clear/>
                                    </IconButton>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={t('Minimal answer count')}
                            fullWidth
                            onChange={(e) => {
                                setMinimalAnswerCount(e.target.value)
                            }}
                            value={minimalAnswerCount}
                        />
                    </Grid>
                    </FilterSection>

                    <FilterSection showIcon title={t('Autopilot')}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="ai-filter-label">{t('AI rating')}</InputLabel>
                            <Select
                                labelId="ai-filter-label"
                                fullWidth
                                value={aiFilter}
                                onChange={(e) => setAiFilter(e.target.value)}
                            >
                                <MenuItem value={aiFilters.aiRated}>{t(aiFilters.aiRated)}</MenuItem>
                                <MenuItem value={aiFilters.aiNotRated}>{t(aiFilters.aiNotRated)}</MenuItem>
                                <MenuItem value={aiFilters.noAiFilter}>{t(aiFilters.noAiFilter)}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <ConversationFeelingSelect
                            feelings={feelings}
                            selectedFeelings={selectedFeelings}
                            setSelectedFeelings={setSelectedFeelings}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleatableSelect
                            label={t('Categories search')}
                            options={Array.isArray(categoriesOptions) ? categoriesOptions : []}
                            selectedOptions={category}
                            setSelectedOptions={setCategory}
                            fullWidth
                        />
                    </Grid>
                    </FilterSection>

                    <FilterSection title={t('Conversation rating filters')}>
                    <Grid item xs={12}>
                        <ConversationStatusSelect
                            statuses={statuses}
                            selectedStatuses={selectedStatuses}
                            setSelectedStatuses={setSelectedStatuses}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleatableSelect
                            label={t('Answer rating')}
                            options={ratingCodes.map((it) => it.code)}
                            selectedOptions={selectedRatings}
                            setSelectedOptions={setSelectedRatings}
                            fullWidth
                        />
                    </Grid>
                    </FilterSection>

                    <FilterSection title={t('Context and memory')}>
                    <Grid item xs={12}>
                        <AutocompleatableSelect
                            label={t('Context')}
                            options={context}
                            selectedOptions={selectedContext}
                            setSelectedOptions={setSelectedContext}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleatableSelect
                            label={t('Context values')}
                            options={contextValues}
                            noOptionsText={t('empty_context')}
                            selectedOptions={selectedContextValues}
                            setSelectedOptions={setSelectedContextValues}
                            fullWidth
                        />
                    </Grid>
                    </FilterSection>

                    <FilterSection title={t('NLP')}>
                    <Grid item xs={12}>
                        <AutocompleatableSelect
                            label={t('Intents')}
                            options={intents}
                            selectedOptions={selectedIntents}
                            setSelectedOptions={setSelectedIntents}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleatableSelect
                            label={t('Entities')}
                            options={entities}
                            selectedOptions={selectedEntities}
                            setSelectedOptions={setSelectedEntities}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleatableSelect
                            label={t('Entity values')}
                            noOptionsText={t('empty_entities')}
                            options={entityValues}
                            selectedOptions={selectedEntityValues}
                            setSelectedOptions={setSelectedEntityValues}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <RangeSelect
                            valueFrom={scoreFrom}
                            valueTo={scoreTo}
                            setValueFrom={setScoreFrom}
                            setValueTo={setScoreTo}
                            fullWidth
                        />
                    </Grid>
                    </FilterSection>

                    <div className="filter-section-actions">
                        <Button
                            onClick={() => copyPathToClipboard()}
                            fullWidth
                        >{t('Copy URL')}</Button>
                    </div>
                </div>
            </MuiThemeProvider>
        </div>
    );

    const tableContent = (
        <div className="flex flex-col w-full">
            <MuiThemeProvider theme={TrainingToolTheme}>
                <div className="w-full custom-table">
                    <Grid container>
                        <Grid item xs={12}>
                            <MaterialTable
                                project={project}
                                projectId={projectId}
                                filter={filter}
                                dirtyConversation={dirtyConversation}
                                onClick={rowClicked}
                            />
                        </Grid>
                    </Grid>
                </div>
            </MuiThemeProvider>
        </div>
    );

    const dialogs = (
        <>
            <Dialog
                open={dialogOpen}
                onClose={(event, reason) => {
                    if (reason === "backdropClick") {
                        handleDialogClose();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="xl"
                aria-labelledby="customized-dialog-title"
            >
                <ConversationDetail
                    projectId={projectId}
                    project={project}
                    handleDialogClose={handleDialogClose}
                    conversationPreview={conversationPreview}
                    setDirtyConversation={setDirtyConversation}
                    onShowPrevious={handleShowPreviousConversation}
                    onShowNext={handleShowNextConversation}
                    hasPrevious={hasPreviousConversation}
                    hasNext={hasNextConversation}
                />
            </Dialog>

            <Dialog
                open={aiDialog}
                onClose={(event, reason) => {
                    if (reason === "backdropClick") {
                        setAiDialog(false);
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="lg"
                aria-labelledby="customized-dialog-title"
            >
                <div className="flex flex-col min-h-80 justify-start items-end">
                    <IconButton
                        aria-label="close"
                        onClick={() => setAiDialog(false)}
                    >
                        <CloseIcon/>
                    </IconButton>
                    {aiReport === '' ? (
                        <div className="flex justify-center items-center h-full m-auto">
                            <CircularProgress/>
                        </div>
                    ) : (
                        <div>{parse(aiReport)}</div>
                    )}
                </div>
            </Dialog>
        </>
    );

    if (renderFilters) {
        return (
            <>
                {filtersContent}
                {dialogs}
            </>
        );
    }
    
    if (renderTable) {
        return (
            <>
                {tableContent}
                {dialogs}
            </>
        );
    }

    return (
        <>
            <div className={`filters-panel-wrapper ${!filtersVisible ? 'filters-hidden' : ''}`}>
                {filtersContent}
            </div>
            {!filtersVisible && (
                <IconButton
                    onClick={() => setFiltersVisible(true)}
                    style={{
                        position: 'fixed',
                        left: '70px',
                        top: '100px',
                        zIndex: 50,
                        backgroundColor: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        padding: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    aria-label={t('Show filters')}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="24" height="24">
                        <path d="M4 10h7.09a6 6 0 0 0 11.82 0H44a1 1 0 0 0 0-2H22.91A6 6 0 0 0 11.09 8H4a1 1 0 0 0 0 2zM17 5a4 4 0 1 1-4 4A4 4 0 0 1 17 5zM44 23H36.91a6 6 0 0 0-11.82 0H4a1 1 0 0 0 0 2H25.09a6 6 0 0 0 11.82 0H44a1 1 0 0 0 0-2zM31 28a4 4 0 1 1 4-4A4 4 0 0 1 31 28zM44 38H22.91a6 6 0 0 0-11.82 0H4a1 1 0 0 0 0 2h7.09a6 6 0 0 0 11.82 0H44a1 1 0 0 0 0-2zM17 43a4 4 0 1 1 4-4A4 4 0 0 1 17 43z" fill="#388e3c" />
                    </svg>
                </IconButton>
            )}
            <div className={`table-content-wrapper ${!filtersVisible ? 'filters-hidden' : ''}`}>
                {tableContent}
            </div>
            {dialogs}
        </>
    );


};

export default MaterialTrainingTool;
